<template>
  <v-container fluid class="pa-3">
    <v-layout row align-center class="mb-3">
      <v-icon color="success" size="30" class="mr-3">mdi-chart-bar</v-icon>
      <div>
        <div class="headline">Deduction Report</div>
        <div class="caption grey--text">
          Each day shows the amount deducted and the balance left from the
          season bonus. Click a row to edit the deduction.
        </div>
      </div>
      <v-spacer></v-spacer>
      <v-btn
        outline
        small
        color="success"
        class="mr-2"
        :disabled="!report"
        :loading="downloading"
        @click="openPdfDialog"
      >
        <v-icon small class="mr-1">mdi-file-pdf-box</v-icon>
        Download PDF
      </v-btn>
      <v-btn outline small color="success" :loading="loading" @click="load">
        <v-icon small class="mr-1">mdi-refresh</v-icon>
        Refresh
      </v-btn>
    </v-layout>

    <v-card flat class="filter-card pa-3 mb-3">
      <v-layout row wrap align-center>
        <v-flex xs12 sm6 md3 class="px-2">
          <v-select
            v-model="season"
            :items="seasons"
            item-text="season"
            item-value="season"
            label="Season"
            prepend-inner-icon="mdi-calendar-range"
            color="success"
            clearable
            hide-details
            @change="onSeason"
          ></v-select>
        </v-flex>
        <v-flex xs6 sm3 md2 class="px-2">
          <v-text-field
            v-model="fromDate"
            label="From"
            type="date"
            color="success"
            hide-details
          ></v-text-field>
        </v-flex>
        <v-flex xs6 sm3 md2 class="px-2">
          <v-text-field
            v-model="toDate"
            label="To"
            type="date"
            color="success"
            hide-details
          ></v-text-field>
        </v-flex>
        <v-flex xs12 sm9 md4 class="px-2">
          <v-autocomplete
            v-model="driverBusId"
            :items="driverOptions"
            item-text="text"
            item-value="value"
            label="Driver / Bus"
            placeholder="All drivers — start typing..."
            prepend-inner-icon="mdi-bus"
            color="success"
            clearable
            hide-details
            :disabled="!report"
          >
            <template v-slot:item="data">
              <v-list-tile-avatar>
                <div class="bus-badge bus-badge--small">
                  {{ data.item.busNumber || "?" }}
                </div>
              </v-list-tile-avatar>
              <v-list-tile-content>
                <v-list-tile-title>{{ data.item.driver }}</v-list-tile-title>
                <v-list-tile-sub-title>
                  ${{ money(data.item.total) }} deducted ·
                  {{ data.item.days }} day{{ data.item.days === 1 ? "" : "s" }}
                </v-list-tile-sub-title>
              </v-list-tile-content>
            </template>
          </v-autocomplete>
        </v-flex>
        <v-flex xs12 sm3 md1 class="px-2 text-xs-right">
          <v-btn color="success" class="ma-0" :loading="loading" @click="load">
            Load
          </v-btn>
        </v-flex>
      </v-layout>
    </v-card>

    <div v-if="loading && !report" class="text-xs-center py-5">
      <v-progress-circular indeterminate color="success"></v-progress-circular>
    </div>

    <v-layout v-if="report" row wrap class="mb-2 stat-row">
      <v-flex xs6 md3 class="pa-1">
        <div class="stat-tile stat-tile--green">
          <v-icon class="stat-icon">mdi-gift</v-icon>
          <div>
            <div class="stat-label">Season bonus</div>
            <div class="stat-value">${{ money(report.yearlyBonus) }}</div>
          </div>
        </div>
      </v-flex>
      <v-flex xs6 md3 class="pa-1">
        <div class="stat-tile stat-tile--orange">
          <v-icon class="stat-icon">mdi-minus-circle</v-icon>
          <div>
            <div class="stat-label">Total deducted</div>
            <div class="stat-value">${{ money(shownTotal) }}</div>
          </div>
        </div>
      </v-flex>
      <v-flex xs6 md3 class="pa-1">
        <div
          v-if="shownDrivers.length === 1"
          class="stat-tile"
          :class="shownDrivers[0].remaining < 0 ? 'stat-tile--red' : 'stat-tile--green'"
        >
          <v-icon class="stat-icon">mdi-wallet</v-icon>
          <div>
            <div class="stat-label">Remaining</div>
            <div class="stat-value">${{ money(shownDrivers[0].remaining) }}</div>
          </div>
        </div>
        <div v-else class="stat-tile stat-tile--grey">
          <v-icon class="stat-icon">mdi-account-multiple</v-icon>
          <div>
            <div class="stat-label">Drivers</div>
            <div class="stat-value">{{ shownDrivers.length }}</div>
          </div>
        </div>
      </v-flex>
      <v-flex xs6 md3 class="pa-1">
        <div class="stat-tile stat-tile--grey">
          <v-icon class="stat-icon">mdi-calendar-remove</v-icon>
          <div>
            <div class="stat-label">Deduction days</div>
            <div class="stat-value">{{ shownDays }}</div>
          </div>
        </div>
      </v-flex>
    </v-layout>

    <v-card v-if="report && !shownDrivers.length" flat class="empty-card pa-5 text-xs-center">
      <v-icon size="40" color="grey lighten-1">mdi-check-circle-outline</v-icon>
      <div class="grey--text mt-2">No deductions in this range.</div>
    </v-card>

    <v-layout v-if="shownDrivers.length" row align-center class="mb-2 px-1">
      <span class="caption grey--text">
        {{ shownDrivers.length }} driver{{ shownDrivers.length === 1 ? "" : "s" }}
      </span>
      <v-spacer></v-spacer>
      <v-btn flat small color="success" class="ma-0" @click="toggleAll">
        <v-icon small class="mr-1">
          {{ allExpanded ? "mdi-unfold-less-horizontal" : "mdi-unfold-more-horizontal" }}
        </v-icon>
        {{ allExpanded ? "Collapse all" : "Expand all" }}
      </v-btn>
    </v-layout>

    <v-card
      v-for="driver in shownDrivers"
      :key="driver.driverBusID"
      class="driver-card mb-3"
    >
      <div class="driver-header">
        <div class="bus-badge">{{ driver.busNumber || "?" }}</div>
        <div class="driver-header-main">
          <div class="driver-name">{{ driver.driver }}</div>
          <div class="caption grey--text">
            {{ driver.lines.length }} deduction day{{ driver.lines.length === 1 ? "" : "s" }}
          </div>
        </div>
        <v-spacer></v-spacer>
        <div class="driver-totals">
          <div class="driver-total-item">
            <div class="stat-label">Deducted</div>
            <div class="driver-total-value orange--text text--darken-3">
              ${{ money(driver.total) }}
            </div>
          </div>
          <div class="driver-total-item">
            <div class="stat-label">Remaining</div>
            <div
              class="driver-total-value"
              :class="driver.remaining < 0 ? 'red--text text--darken-2' : 'green--text text--darken-3'"
            >
              ${{ money(driver.remaining) }}
            </div>
          </div>
        </div>
      </div>
      <v-progress-linear
        v-if="report.yearlyBonus > 0"
        class="ma-0"
        :value="usedPercent(driver)"
        :color="usedPercent(driver) >= 100 ? 'red' : 'orange darken-1'"
        background-color="green lighten-4"
        height="4"
      ></v-progress-linear>

      <div
        v-for="line in driver.lines"
        :key="lineKey(driver, line)"
        class="line-wrap"
        :class="{ 'line-wrap--open': isExpanded(driver, line) }"
      >
        <div class="line-row" title="Edit deduction" @click="openLine(driver, line)">
          <div class="date-block">
            <div class="date-block-month">{{ monthShort(line.date) }}</div>
            <div class="date-block-day">{{ dayOfMonth(line.date) }}</div>
          </div>
          <div class="line-date-text">
            <div class="font-weight-medium">{{ weekday(line.date) }}</div>
            <div class="caption grey--text">{{ formatDate(line.date) }}</div>
          </div>

          <div class="line-info">
            <div class="line-meta">
              <div class="line-meta-item">
                <div class="line-meta-label">Route category</div>
                <div class="line-meta-value">{{ line.categoryCode || "—" }}</div>
              </div>
              <div class="line-meta-item">
                <div class="line-meta-label">Deduction category</div>
                <div class="line-meta-value">{{ line.preset || "—" }}</div>
              </div>
            </div>
            <div v-if="line.enteredBy" class="caption grey--text">
              by {{ line.enteredBy }}
            </div>
          </div>

          <div
            class="absence-toggle"
            :class="{ 'absence-toggle--open': isExpanded(driver, line) }"
            title="Show absences"
            @click.stop="toggleLine(driver, line)"
          >
            <v-icon small class="mr-1">mdi-account-off</v-icon>
            <span class="absence-count">{{ line.absenceCount || 0 }}</span>
            <span class="ml-1 hidden-xs-only">
              absence{{ line.absenceCount === 1 ? "" : "s" }}
            </span>
            <v-icon small class="ml-1 absence-chevron">mdi-chevron-down</v-icon>
          </div>

          <div class="ledger-amount">-${{ money(line.amount) }}</div>
          <div
            class="ledger-balance"
            :class="{ 'red--text text--darken-2': line.balance < 0 }"
          >
            ${{ money(line.balance) }}
          </div>
        </div>

        <v-expand-transition>
          <div v-if="isExpanded(driver, line)" class="absence-panel">
            <div v-if="!line.absenceItems || !line.absenceItems.length" class="caption grey--text">
              No absence rows found for this day.
            </div>
            <template v-for="group in groupAbsences(line.absenceItems)">
              <div :key="'g-' + group.name" class="absence-group-header">
                {{ group.name }}
                <span class="grey--text">({{ group.rows.length }})</span>
              </div>
              <div
                v-for="(absence, aIndex) in group.rows"
                :key="group.name + '-' + aIndex"
                class="absence-item"
              >
                <div class="absence-item-main">
                  <div class="absence-time">
                    <v-icon small color="grey">mdi-clock-outline</v-icon>
                    {{ absenceTimeText(absence) }}
                  </div>
                  <div
                    v-if="absence.route && absence.route !== 'No route'"
                    class="absence-route"
                  >
                    {{ absence.route }}
                  </div>
                  <div v-if="absence.reason" class="absence-reason">
                    {{ absence.reason }}
                  </div>
                </div>
                <div v-if="absence.note" class="absence-note">{{ absence.note }}</div>
              </div>
            </template>
            <div v-if="line.noDeductReason" class="no-deduct-note">
              <v-icon small color="grey darken-1" class="mr-1">mdi-comment-text-outline</v-icon>
              {{ line.noDeductReason }}
            </div>
          </div>
        </v-expand-transition>
      </div>
    </v-card>

    <v-dialog v-model="pdfDialog" max-width="420">
      <v-card>
        <v-card-title class="success white--text py-2 px-4">
          <span class="title pt-1">Download PDF</span>
          <v-spacer></v-spacer>
          <v-btn class="white--text my-0" icon flat small dark @click="pdfDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-4">
          <div class="caption grey--text mb-2">
            Each driver starts on a new page. Pick one month, or all months of the season.
          </div>
          <v-select
            v-model="pdfChoice"
            :items="pdfMonthOptions"
            item-text="label"
            item-value="value"
            label="Month"
            prepend-inner-icon="mdi-calendar"
            color="success"
            hide-details
          ></v-select>
        </v-card-text>
        <v-card-actions class="px-2 py-3">
          <v-spacer></v-spacer>
          <v-btn dark color="default" @click="pdfDialog = false">Cancel</v-btn>
          <v-btn color="success" :loading="downloading" @click="downloadPdf">
            Download
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <deduction-detail-dialog
      v-model="editDialog"
      :row="editRow"
      @saved="load"
    ></deduction-detail-dialog>
  </v-container>
</template>

<script>
import moment from "moment";
import deductionDetailDialog from "./components/DeductionDetailDialog.vue";

export default {
  components: {
    deductionDetailDialog,
  },
  data() {
    return {
      loading: false,
      downloading: false,
      season: "",
      seasons: [],
      fromDate: "",
      toDate: "",
      driverBusId: null,
      report: null,
      expanded: {},
      pdfDialog: false,
      pdfChoice: "all",
      editDialog: false,
      editRow: null,
    };
  },
  computed: {
    driverOptions() {
      return (this.report ? this.report.drivers : [])
        .map((driver) => ({
          text: "#" + (driver.busNumber || "") + " " + (driver.driver || ""),
          value: driver.driverBusID,
          busNumber: driver.busNumber,
          driver: driver.driver,
          total: driver.total,
          days: (driver.lines || []).length,
        }))
        .sort(
          (a, b) =>
            this.compareBus(a.busNumber, b.busNumber) ||
            (a.driver || "").localeCompare(b.driver || "")
        );
    },
    seasonMonths() {
      const picked = this.seasons.find((s) => s.season === this.season);
      const start = picked && picked.fromDate
        ? moment(picked.fromDate)
        : this.fromDate
        ? moment(this.fromDate)
        : null;
      const end = picked && picked.toDate
        ? moment(picked.toDate)
        : this.toDate
        ? moment(this.toDate)
        : null;
      if (!start || !end || !start.isValid() || !end.isValid()) return [];
      const months = [];
      const cursor = start.clone().startOf("month");
      const last = end.clone().startOf("month");
      while (cursor.isSameOrBefore(last, "month")) {
        months.push({
          value: cursor.format("YYYY-MM"),
          label: cursor.format("MMMM YYYY"),
        });
        cursor.add(1, "month");
      }
      return months;
    },
    pdfMonthOptions() {
      return [{ value: "all", label: "All months" }].concat(this.seasonMonths);
    },
    shownDrivers() {
      if (!this.report) return [];
      if (!this.driverBusId) return this.report.drivers || [];
      return (this.report.drivers || []).filter(
        (d) => d.driverBusID === this.driverBusId
      );
    },
    shownTotal() {
      return this.shownDrivers.reduce((sum, d) => sum + Number(d.total || 0), 0);
    },
    shownDays() {
      return this.shownDrivers.reduce((sum, d) => sum + (d.lines || []).length, 0);
    },
    allExpanded() {
      const keys = [];
      this.shownDrivers.forEach((d) =>
        (d.lines || []).forEach((l) => keys.push(this.lineKey(d, l)))
      );
      return keys.length > 0 && keys.every((k) => this.expanded[k]);
    },
  },
  watch: {
    season(name) {
      if (name) localStorage.setItem("deductionReportSeason", name);
      else localStorage.removeItem("deductionReportSeason");
    },
  },
  async created() {
    await this.loadSeasons();
    await this.load();
  },
  methods: {
    money(value) {
      return Number(value || 0).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    compareBus(a, b) {
      const rank = (value) => {
        const text = String(value || "").trim();
        if (/^\d+$/.test(text)) return parseInt(text, 10);
        const digits = text.replace(/\D/g, "");
        return digits ? parseInt(digits, 10) : Number.MAX_SAFE_INTEGER;
      };
      const left = rank(a);
      const right = rank(b);
      if (left !== right) return left - right;
      return String(a || "").localeCompare(String(b || ""));
    },
    absenceTimeText(absence) {
      if (absence.timeStart) return absence.timeStart;
      if (!absence.route || absence.route === "No route") return "No route";
      return "No time";
    },
    formatDate(value) {
      return value ? moment(value).format("M/D/YYYY") : "";
    },
    monthShort(value) {
      return moment(value).format("MMM");
    },
    dayOfMonth(value) {
      return moment(value).format("D");
    },
    weekday(value) {
      return moment(value).format("dddd");
    },
    usedPercent(driver) {
      const bonus = Number(this.report.yearlyBonus || 0);
      if (bonus <= 0) return 0;
      return Math.min(100, Math.max(0, (Number(driver.total || 0) / bonus) * 100));
    },
    lineKey(driver, line) {
      return (
        driver.driverBusID + "|" + moment(line.date).format("YYYY-MM-DD") + "|" + (line.kind || "")
      );
    },
    isExpanded(driver, line) {
      return !!this.expanded[this.lineKey(driver, line)];
    },
    toggleLine(driver, line) {
      const key = this.lineKey(driver, line);
      this.$set(this.expanded, key, !this.expanded[key]);
    },
    toggleAll() {
      const next = !this.allExpanded;
      const expanded = {};
      if (next) {
        this.shownDrivers.forEach((d) =>
          (d.lines || []).forEach((l) => {
            expanded[this.lineKey(d, l)] = true;
          })
        );
      }
      this.expanded = expanded;
    },
    // Same AM / PM / CUSTOM ordering as the absences panel on the Deductions page.
    groupAbsences(items) {
      if (!items || !items.length) return [];
      const order = ["AM", "PM", "CUSTOM", "FAM", "PPM"];
      const groups = {};
      items.forEach((item) => {
        const name = item.groupBy || "Other";
        if (!groups[name]) groups[name] = [];
        groups[name].push(item);
      });
      return Object.keys(groups)
        .sort((a, b) => {
          const ai = order.indexOf(a);
          const bi = order.indexOf(b);
          return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
        })
        .map((name) => ({ name: name, rows: groups[name] }));
    },
    async loadSeasons() {
      this.seasons = (await this.$http.get("api/driverAbsent/getDeductionSeasons")) || [];
      const today = moment();
      const current = this.seasons.find(
        (s) =>
          s.fromDate &&
          moment(s.fromDate).isSameOrBefore(today, "day") &&
          (!s.toDate || moment(s.toDate).isSameOrAfter(today, "day"))
      );
      const picked = current || this.seasons[0];
      if (picked) {
        this.season = picked.season;
        this.fromDate = picked.fromDate
          ? moment(picked.fromDate).format("YYYY-MM-DD")
          : "";
        this.toDate = picked.toDate
          ? moment(picked.toDate).format("YYYY-MM-DD")
          : moment().format("YYYY-MM-DD");
      }
    },
    onSeason(name) {
      const picked = this.seasons.find((s) => s.season === name);
      if (!picked) return;
      this.fromDate = picked.fromDate
        ? moment(picked.fromDate).format("YYYY-MM-DD")
        : this.fromDate;
      this.toDate = picked.toDate
        ? moment(picked.toDate).format("YYYY-MM-DD")
        : this.toDate;
    },
    async load() {
      if (!this.fromDate || !this.toDate) return;
      this.loading = true;
      try {
        this.report = await this.$http.get(
          "api/driverAbsent/getDeductionReport?fromDate=" +
            this.fromDate +
            "&toDate=" +
            this.toDate +
            "&season=" +
            encodeURIComponent(this.season || "")
        );
        const stillThere = (this.report.drivers || []).some(
          (d) => d.driverBusID === this.driverBusId
        );
        if (!stillThere) this.driverBusId = null;
        this.expanded = {};
      } catch (e) {
        this.report = null;
        this.$notify({
          group: "notification",
          type: "error",
          text: (e && e.body) || "Failed to load the report",
        });
      } finally {
        this.loading = false;
      }
    },
    openPdfDialog() {
      this.pdfChoice = "all";
      this.pdfDialog = true;
    },
    pdfRange() {
      const picked = this.seasons.find((s) => s.season === this.season);
      if (this.pdfChoice === "all") {
        if (picked) {
          return {
            from: picked.fromDate
              ? moment(picked.fromDate).format("YYYY-MM-DD")
              : this.fromDate,
            to: picked.toDate
              ? moment(picked.toDate).format("YYYY-MM-DD")
              : this.toDate,
            forMonth: false,
          };
        }
        return { from: this.fromDate, to: this.toDate, forMonth: false };
      }

      const month = moment(this.pdfChoice + "-01");
      let start = month.clone().startOf("month");
      let end = month.clone().endOf("month");
      if (picked && picked.fromDate && moment(picked.fromDate).isAfter(start, "day")) {
        start = moment(picked.fromDate);
      }
      if (picked && picked.toDate && moment(picked.toDate).isBefore(end, "day")) {
        end = moment(picked.toDate);
      }
      return {
        from: start.format("YYYY-MM-DD"),
        to: end.format("YYYY-MM-DD"),
        forMonth: true,
      };
    },
    openLine(driver, line) {
      this.editRow = {
        driverBusID: driver.driverBusID,
        driver: driver.driver,
        busNumber: driver.busNumber,
        date: line.date,
        kind: line.kind,
      };
      this.editDialog = true;
    },
    async downloadPdf() {
      const range = this.pdfRange();
      if (!range.from || !range.to) return;
      this.downloading = true;
      const url =
        "api/driverAbsent/downloadDeductionReport?fromDate=" +
        range.from +
        "&toDate=" +
        range.to +
        "&driverBusId=" +
        (this.driverBusId || "") +
        "&season=" +
        encodeURIComponent(this.season || "") +
        "&forMonth=" +
        (range.forMonth ? "true" : "false");
      try {
        // Use the app's authenticated axios instance ($http) — a plain axios call skips the
        // auth-token request interceptor and comes back as a 401.
        const response = await this.$http.get(url, { responseType: "blob" });
        const blobUrl = window.URL.createObjectURL(new Blob([response]));
        const link = document.createElement("a");
        link.href = blobUrl;
        link.setAttribute("download", "DeductionReport.pdf");
        document.body.appendChild(link);
        link.click();
        link.remove();
        this.pdfDialog = false;
      } catch (e) {
        this.$notify({
          group: "notification",
          type: "error",
          text: (e && e.body) || "Failed to download the report",
        });
      } finally {
        this.downloading = false;
      }
    },
  },
};
</script>

<style scoped>
.filter-card.v-card {
  border: 1px solid #e0e0e0 !important;
  border-radius: 8px !important;
  background: #fff;
}

/* Summary tiles */
.stat-tile {
  display: flex;
  align-items: center;
  height: 100%;
  padding: 14px 16px;
  border-radius: 8px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-left: 4px solid #9e9e9e;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}
.stat-icon.v-icon {
  font-size: 28px;
  margin-right: 14px;
  color: #9e9e9e;
}
.stat-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #757575;
}
.stat-value {
  font-size: 22px;
  font-weight: 600;
  line-height: 1.3;
  color: #424242;
}
.stat-tile--green {
  border-left-color: #43a047;
}
.stat-tile--green .stat-icon.v-icon,
.stat-tile--green .stat-value {
  color: #2e7d32;
}
.stat-tile--orange {
  border-left-color: #fb8c00;
}
.stat-tile--orange .stat-icon.v-icon,
.stat-tile--orange .stat-value {
  color: #e65100;
}
.stat-tile--red {
  border-left-color: #e53935;
}
.stat-tile--red .stat-icon.v-icon,
.stat-tile--red .stat-value {
  color: #c62828;
}

.empty-card.v-card {
  border: 1px dashed #cfd8dc !important;
  border-radius: 8px !important;
}

/* Driver card */
.driver-card.v-card {
  border-radius: 8px !important;
  overflow: hidden;
}
.driver-header {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: linear-gradient(90deg, #e8f5e9, #f7fbf7);
  border-bottom: 1px solid #c8e6c9;
}
.bus-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  height: 44px;
  padding: 0 8px;
  border-radius: 22px;
  background: #43a047;
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  box-shadow: 0 2px 4px rgba(67, 160, 71, 0.35);
}
.bus-badge--small {
  min-width: 32px;
  height: 32px;
  font-size: 12px;
  box-shadow: none;
}
.driver-header-main {
  margin-left: 14px;
}
.driver-name {
  font-size: 17px;
  font-weight: 600;
  color: #263238;
}
.driver-totals {
  display: flex;
}
.driver-total-item {
  text-align: right;
  margin-left: 28px;
}
.driver-total-value {
  font-size: 18px;
  font-weight: 600;
}

/* Deduction line */
.line-wrap {
  border-bottom: 1px solid #eeeeee;
}
.line-wrap:last-child {
  border-bottom: none;
}
.line-wrap--open {
  background: #fafdfa;
}
.line-row {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  cursor: pointer;
  transition: background-color 0.15s;
}
.line-row:hover {
  background: #f5f9f5;
}
.date-block {
  flex: 0 0 46px;
  text-align: center;
  border: 1px solid #c8e6c9;
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
}
.date-block-month {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  color: #fff;
  background: #43a047;
  padding: 1px 0;
}
.date-block-day {
  font-size: 18px;
  font-weight: 600;
  color: #2e7d32;
  line-height: 1.5;
}
.line-date-text {
  flex: 0 0 120px;
  margin-left: 12px;
}
.line-info {
  flex: 1 1 auto;
  min-width: 0;
}
.line-meta {
  display: flex;
  flex-wrap: wrap;
}
.line-meta-item {
  margin-right: 22px;
}
.line-meta-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #78909c;
}
.line-meta-value {
  font-size: 14px;
  font-weight: 600;
  color: #37474f;
}
.ledger-amount,
.ledger-balance {
  flex: 0 0 110px;
  text-align: right;
  font-size: 15px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.ledger-amount {
  margin-left: 16px;
  font-weight: 500;
  color: #e65100;
}
.ledger-balance {
  font-weight: 700;
  color: #263238;
}
.absence-toggle {
  display: inline-flex;
  align-items: center;
  flex: 0 0 auto;
  padding: 5px 12px;
  border-radius: 16px;
  border: 1px solid #e0e0e0;
  background: #fff;
  color: #546e7a;
  font-size: 13px;
  transition: background-color 0.15s, border-color 0.15s;
}
.line-row:hover .absence-toggle {
  border-color: #a5d6a7;
}
.absence-toggle:hover {
  background: #e8f5e9;
}
.absence-toggle--open {
  background: #e8f5e9;
  border-color: #66bb6a;
  color: #2e7d32;
}
.absence-toggle .v-icon {
  color: inherit;
}
.absence-count {
  font-weight: 700;
}
.absence-chevron {
  transition: transform 0.2s;
}
.absence-toggle--open .absence-chevron {
  transform: rotate(180deg);
}

/* Expanded absences */
.absence-panel {
  margin: 0 16px 12px 74px;
  padding: 8px 14px 10px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-left: 3px solid #66bb6a;
  border-radius: 6px;
}
.absence-group-header {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  font-weight: 600;
  color: #757575;
  margin: 10px 0 2px;
}
.absence-group-header:first-child {
  margin-top: 2px;
}
.absence-item {
  display: block;
  padding: 5px 0;
  font-size: 13px;
  border-bottom: 1px solid #f5f5f5;
}
.absence-item-main {
  display: flex;
  align-items: center;
}
.absence-note {
  margin: 1px 0 2px 100px;
  padding-left: 8px;
  border-left: 2px solid #cfd8dc;
  font-size: 12px;
  color: #455a64;
  line-height: 1.3;
}
.absence-item:last-child {
  border-bottom: none;
}
.absence-time {
  flex: 0 0 100px;
  color: #616161;
}
.absence-route {
  flex: 1 1 auto;
  font-weight: 500;
}
.absence-reason {
  flex: 0 0 auto;
  max-width: 45%;
  padding: 1px 10px;
  border-radius: 10px;
  background: #fff3e0;
  color: #e65100;
  font-size: 12px;
}
.no-deduct-note {
  margin-top: 8px;
  padding-top: 6px;
  border-top: 1px dashed #e0e0e0;
  font-size: 12px;
  color: #616161;
}
</style>
